import React, {
  FC,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import * as d3 from 'd3'
import * as topojson from 'topojson-client'
import styled from 'styled-components'
import unproductive from './Assets/unproduktive_gebiete_lv95.json'
import ZoomOutIcon from './Assets/ZoomOut'
import ZoomInIcon from './Assets/ZoomIn'
import InfoIcon from './Assets/Info'
import FullScreenIcon from './Assets/FullScreen'
import ExitFullScreenIcon from './Assets/ExitFullScreen'
import { useIntersectionObserver } from 'usehooks-ts'
import { SVGDefs } from './SVGDefs'
import { LayerStyles } from 'Types/MicroFrontendProps'
import { useMFContext } from 'components/MFHelpers'

const defaultLayerStyles: LayerStyles = {
  municipalityHover: {
    stroke: 'var(--text-color)',
    strokeWidth: '2.5',
  },
  municipality: {
    stroke: 'var(--site-background)',
    strokeWidth: '0.5',
  },
  activeMunicipality: {
    stroke: 'var(--brandblue-main)',
    strokeWidth: '4',
  },
  unproductive: {
    stroke: 'var(--brandblue-main)',
    strokeWidth: '4',
  },
  lakes: {
    stroke: 'var(--brandblue-main)',
    strokeWidth: '4',
  },
}

const INITIAL_HEIGHT = 680
const INITIAL_WIDTH = 975
const MAX_ZOOM = 5
const EXTENT_PADDING = 100
export interface Props {
  indexOfPropertyToCompare?: number
  municipalityData?: { bfsId: number; [key: string]: any }[]

  year: number
  topoJson: any
  activeMunicipalityId?: number
  newActiveMunicipalityIdHandler: (newId: number) => void
  hoverHandler: (newData: any) => void
  isZoomable?: boolean
  zoomIntoActiveMunicipality: boolean
  disableTouchOnMobile?: boolean
  activeStrokeWidth: string
  activeStrokeColor: string
  onZoomOut?: () => void
  municipalityFallbackFillColor: string

  toolTipRef: React.RefObject<HTMLDivElement>
  fullscreenChangeHandler?: (isFullscreen: boolean) => void
  isFullscreen?: boolean
  scrollIntoView?: boolean
  layerStyles?: LayerStyles
  colorMode?: 'dark' | 'light'
  color?: (n: any) => string | d3.RGBColor | d3.HSLColor
}
const ChoroplethMap: FC<Props> = ({
  municipalityData,
  indexOfPropertyToCompare,
  year,
  topoJson,
  activeMunicipalityId,
  newActiveMunicipalityIdHandler,
  hoverHandler,
  isZoomable = true,
  zoomIntoActiveMunicipality = true,
  disableTouchOnMobile,
  municipalityFallbackFillColor,
  onZoomOut,
  toolTipRef,
  fullscreenChangeHandler,
  isFullscreen,
  scrollIntoView,
  layerStyles = {},
  color,
  colorMode = 'light',
}): ReactElement => {
  layerStyles = {
    ...defaultLayerStyles,
    ...layerStyles,
  } as Required<LayerStyles>
  const [chTopoJSON, setChTopoJSON] = useState<any | null>()
  const [path, setPath] = useState<d3.GeoPath | null>()
  const [geoJsonMuni, setGeoJsonMuni] = useState<any | null>()
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [height, setHeight] = useState(INITIAL_HEIGHT)
  const [headerHeight, setHeaderHeight] = useState(0)

  const [hoverPosition, setHoverPosition] = useState<
    { x: number; y: number } | undefined
  >()
  const [hoverShape, setHoverShape] = useState<string | undefined>()
  const [activeShape, setActiveShape] = useState<string | undefined>()
  const [showInfo, setShowInfo] = useState(false)
  const [width, setWidth] = useState(INITIAL_WIDTH)
  const [isZooming, setIsZooming] = useState(false)
  const [activeMunicipalitySelection, setActiveMunicipalitySelection] =
    useState<d3.Selection<d3.BaseType, unknown, null, undefined> | null>(null)
  const mapControlsRef = useRef<HTMLDivElement | null>(null)

  const countryBorderRef = useRef<SVGPathElement | null>(null)
  const municipalityLayerRef = useRef<SVGPathElement | null>(null)
  const swissChoroplethMapRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const { isMobile, currentLanguage } = useMFContext()

  const zoomed = (event: any) => {
    const { transform } = event
    setZoomLevel(transform.k)
    setIsZoomedIn(transform.k > 1)
    event.sourceEvent?.stopImmediatePropagation()

    // Only allow drag if zoomed in
    if (transform.k === 1) {
      d3.select(svgRef.current).call(zoom.filter(() => false) as any)
    } else {
      d3.select(svgRef.current).call(zoom.filter(filterZoomEvents) as any)
    }

    d3.select(svgRef.current).selectChildren().attr('transform', transform)
  }

  const filterZoomEvents = (event: any) => {
    // Don't allow drag when at zoom level 1
    if (event.type === 'mousedown' && zoomLevel === 1) {
      return false
    }

    const isTouchScrolling =
      event?.type === 'touchstart' &&
      event?.touches.length === 1 &&
      zoomLevel <= 1

    return (
      !(!event?.ctrlKey && event?.type === 'wheel' && !isFullscreen) &&
      !isTouchScrolling
    )
  }

  const zoom = d3
    .zoom()
    .filter(filterZoomEvents)
    .scaleExtent([1, MAX_ZOOM + 3])
    .translateExtent([
      [0 - EXTENT_PADDING, 0 - EXTENT_PADDING],
      [INITIAL_WIDTH + EXTENT_PADDING, INITIAL_HEIGHT + EXTENT_PADDING],
    ])
    .on('zoom', zoomed)
  d3.select(svgRef.current).call(zoom as any)

  const setTopoJson = (newTopoJson: any) => {
    setGeoJsonMuni(
      topojson.feature(newTopoJson, newTopoJson.objects.municipalities)
    )
    setChTopoJSON(newTopoJson)
    const projection = d3
      .geoMercator()
      .rotate([0, 0])
      .center([8.23, 46.885])
      .scale(11980)
      .translate([width / 2, height / 2])
      .precision(0.1)
    setPath(() => d3.geoPath(projection))
  }

  function getValueToCompare(bfsIdToFind: number): any | undefined {
    if (!municipalityData) return undefined
    const foundInMunicipaltyData = municipalityData.find(
      (muni) => +muni.bfsId === +bfsIdToFind
    )
    return foundInMunicipaltyData
  }
  function repositionToolTip(mouseX: number, mouseY: number) {
    const toolTipElement = d3.select(toolTipRef.current).node() as HTMLElement
    const tooltipParentElement = toolTipRef.current?.parentElement
    if (!toolTipElement) return

    let left = mouseX + 10
    let top = mouseY + 10
    let parentWidth = tooltipParentElement?.clientWidth
    let parentHeight = tooltipParentElement?.clientHeight
    let tooltipShouldBeOnLeft = left + toolTipElement.clientWidth > parentWidth
    let tooltipShouldBeOnTop = top + toolTipElement.clientHeight > parentHeight

    if (tooltipShouldBeOnLeft) {
      left = mouseX - toolTipElement.clientWidth - 10
    }
    if (tooltipShouldBeOnTop) {
      top = mouseY - toolTipElement.clientHeight - 10
    }
    // Only update the tooltip position, not the marker
    d3.select(toolTipRef.current)
      .style('left', left + 'px')
      .style('top', top + 'px')
  }
  function addHoverInteractions() {
    if (
      // mapElement?.clientWidth > 600 &&
      d3.select(municipalityLayerRef.current).selectChildren().nodes().length >
      0
    ) {
      d3.select(municipalityLayerRef.current)
        .selectChildren()
        .on('mouseover', function (this: any, event: MouseEvent) {
          if (isZooming || isMobile) return
          d3.select(toolTipRef.current).style('opacity', 1)

          if (municipalityData) {
            let muni = municipalityData.find((muni) => {
              return +muni.bfsId === +d3.select(this).attr('id').split('-')[1]
            })
            hoverHandler(muni)
          }

          // put active municipality over the hovered one
          d3.select(municipalityLayerRef.current)
            .selectChild('.active-municipality')
            .raise()

          repositionToolTip(event.offsetX, event.offsetY)
        })
        .on('mouseout', function () {
          d3.select(toolTipRef.current)
            .style('opacity', 0)
            .style('top', 0)
            .style('left', 0)
          hoverHandler(undefined)
        })
        .on('mousemove', function (event: MouseEvent) {
          if (isZooming || isMobile) return
          // Only update tooltip position, not the marker position
          repositionToolTip(event.offsetX, event.offsetY)
        })

      window.addEventListener(
        'scroll',
        function () {
          d3.select(toolTipRef.current).style('opacity', 0)
        },
        false
      )
    }
  }
  function zoomTo(
    selection: d3.Selection<d3.BaseType, unknown, null, undefined>
  ) {
    setIsZooming(true)
    const d = geoJsonMuni.features.find(
      (muni: any) => +muni.properties.id === +selection.attr('id').split('-')[1]
    )
    const [[x0, y0], [x1, y1]] = path!.bounds(d)

    d3.select(svgRef.current)
      .transition()
      .duration(750)
      .call(
        zoom.transform as any,
        d3.zoomIdentity
          .translate(INITIAL_WIDTH / 2, INITIAL_HEIGHT / 2)
          .scale(
            Math.min(
              MAX_ZOOM,
              0.9 /
                Math.max((x1 - x0) / INITIAL_WIDTH, (y1 - y0) / INITIAL_HEIGHT)
            )
          )
          .translate(-(x0 + x1) / 2, -(y0 + y1) / 2)
      )
      .on('end', () => setIsZooming(false))

    setIsZoomedIn(true)
  }
  function zoomInToWhereIam() {
    const svgSelection = d3.select(svgRef.current)
    if (!svgSelection || !zoom) return
    svgSelection.transition().call(zoom.scaleBy as any, 2)
  }
  function clearMarker() {
    setHoverPosition(undefined)
    setActiveShape(undefined)
  }
  function zoomOut() {
    const svgSelection = d3.select(svgRef.current)
    if (path && svgSelection) {
      svgSelection
        .transition()
        .duration(750)
        .call(
          zoom.transform as any,
          d3.zoomIdentity,
          d3.zoomTransform(svgSelection.node()!).invert([width / 2, height / 2])
        )
      setIsZoomedIn(false)
      clearMarker()
    }
    if (onZoomOut) {
      onZoomOut()
    }
  }
  function zoomToActiveMunicipality(passedActiveMunicipalityId?: number) {
    const bfsIdToZoomTo = passedActiveMunicipalityId || activeMunicipalityId

    if (bfsIdToZoomTo && activeMunicipalitySelection) {
      if (activeMunicipalitySelection.node()) {
        var box = (activeMunicipalitySelection as any).node().getBBox()

        setHoverPosition({
          x: box.x + box.width / 2,
          y: box.y + box.height / 2 - 20,
        })
        setActiveShape(activeMunicipalitySelection.attr('d'))
      }

      zoomTo(activeMunicipalitySelection!)
    }
  }
  function scrollToTargetAdjusted(target: Element) {
    var elementPosition = target.getBoundingClientRect().top
    var offsetPosition = elementPosition + window.scrollY - headerHeight

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })
  }
  function highlightActiveMunicipality() {
    activeMunicipalitySelection?.attr('class', 'municipality')
    const muniToHighlight = d3
      .select(municipalityLayerRef.current)
      .selectChild('#bfsId-' + activeMunicipalityId)
      ?.attr('class', 'active-municipality')

    if (activeMunicipalityId && muniToHighlight.node()) {
      muniToHighlight.raise()
      setActiveMunicipalitySelection(muniToHighlight)
      isZoomedIn && zoomTo(muniToHighlight)
    }
  }
  function addMunicipalityPaths() {
    if (path) {
      const allMunipaths = d3
        .select(municipalityLayerRef.current)
        .selectChildren()
      allMunipaths.remove()

      allMunipaths
        .data(geoJsonMuni.features)
        .enter()
        .append('path')
        .attr('d', (feature: any) => path(feature))
        .attr('class', 'municipality')
        .attr('stroke', layerStyles.municipality?.stroke || '#fff')
        .attr('stroke-width', layerStyles.municipality?.strokeWidth || '1')
        .attr('id', (feature: any) => {
          return 'bfsId-' + feature.properties.id
        })
        .attr('bfsId', (feature: any) => feature.id)
        .on('click', function (this: any, event: PointerEvent, feature: any) {
          var t = d3.select(this)
          var box = t.node()?.getBBox()

          // Set the marker position on click
          setHoverPosition({
            x: box.x + box.width / 2,
            y: box.y + box.height / 2 - 20,
          })
          setActiveShape(t.attr('d'))
          t.raise()

          if (
            (window.innerWidth > 768 || !disableTouchOnMobile) &&
            feature.properties.id
          ) {
            newActiveMunicipalityIdHandler(
              feature.properties.id ?? feature.properties.id
            )
          }
        })
        .on('mouseenter', function (d) {
          if (isMobile) return
          setHoverShape(d3.select(this).attr('d'))
        })
        .on('mouseleave', function (d) {
          if (isMobile) return
          setHoverShape(undefined)
        })
      colorMunicipalities()
      addHoverInteractions()
    }
  }
  function colorMunicipalities() {
    if (d3.select(municipalityLayerRef.current).selectAll('path').size() === 0)
      addMunicipalityPaths()

    d3.select(municipalityLayerRef.current)
      .selectAll('path')
      .attr('fill', (feature: any) => {
        const valueToCompare = getValueToCompare(feature.properties.id)
        let newColor
        if (valueToCompare !== undefined && color) {
          newColor = color(valueToCompare)
        } else {
          newColor = municipalityFallbackFillColor
        }
        return newColor as string //!.toString()
      })
  }
  function fullscreenButtonPressed() {
    setHeight(window.innerHeight)
    setWidth(swissChoroplethMapRef.current?.clientWidth ?? 0)
    fullscreenChangeHandler?.(!isFullscreen)
    setTimeout(() => {
      zoomOut()
      svgRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }, 100)
  }

  useEffect(() => {
    // if we already have the (local) topojson, we don't need to fetch it
    if (topoJson) {
      setTopoJson(topoJson)
      return
    }
    fetch('https://unpkg.com/swiss-maps/' + year + '/ch-combined.json').then(
      (res) =>
        res.json().then((parsed) => {
          setTopoJson(parsed)
        })
    )
  }, [topoJson, year])

  useEffect(() => {
    if (geoJsonMuni && path) {
      addMunicipalityPaths()
    }
    activeMunicipalityId &&
      zoomIntoActiveMunicipality &&
      zoomToActiveMunicipality()
  }, [geoJsonMuni, path, municipalityData, activeMunicipalityId])

  useEffect(() => {
    zoomIntoActiveMunicipality ? zoomToActiveMunicipality() : zoomOut()
  }, [zoomIntoActiveMunicipality])

  useEffect(() => {
    if (activeMunicipalitySelection && zoomIntoActiveMunicipality) {
      zoomToActiveMunicipality()
    }
  }, [activeMunicipalitySelection])

  useEffect(() => {
    highlightActiveMunicipality()
    if (!activeMunicipalityId) {
      zoomOut()
      clearMarker()
    }
    scrollIntoView &&
      activeMunicipalityId &&
      swissChoroplethMapRef.current &&
      scrollToTargetAdjusted(swissChoroplethMapRef.current)
  }, [activeMunicipalityId])

  useEffect(() => {
    colorMunicipalities()
    highlightActiveMunicipality()
  }, [municipalityData, indexOfPropertyToCompare, color])

  useEffect(() => {
    const headerElement = document.querySelector('[class*=FullHeader_root]')

    let headerHeight = 0
    if (headerElement) {
      // check if header is sticky
      const headerStyle = window.getComputedStyle(headerElement)
      const isSticky =
        headerStyle.position === 'sticky' && headerStyle.top === '0px'

      if (isSticky) {
        headerHeight = headerElement.clientHeight
        setHeaderHeight(headerHeight)
      }
    }

    addMunicipalityPaths()
  }, [])

  return (
    <StylesWrapper
      activeStroke={layerStyles.activeMunicipality?.stroke || 'transparent'}
      activeStrokeWidth={
        layerStyles.activeMunicipality?.activeStrokeWidth || '0'
      }
    >
      <div id='swiss-choropleth-map' ref={swissChoroplethMapRef}>
        {chTopoJSON && path && (
          <svg
            style={{
              height: isFullscreen ? height : undefined,
              width: isFullscreen ? width : undefined,
            }}
            viewBox={'0,0,' + INITIAL_WIDTH + ',' + INITIAL_HEIGHT}
            ref={svgRef}
          >
            <SVGDefs />

            <g id='layers' transform='translate(0,20)'>
              <g
                fill='none'
                stroke='#000'
                strokeLinejoin='round'
                strokeLinecap='round'
              >
                {/* {chTopoJSON.objects.country && (
                  <g className='country-bg-layer'>
                    <path
                      id='country-border'
                      ref={countryBorderRef}
                      stroke='none'
                      fill='#fff'
                      style={{ pointerEvents: 'none' }}
                      d={
                        path(
                          topojson.feature(
                            chTopoJSON,
                            chTopoJSON.objects.country
                          )
                        ) as string
                      }
                    />
                  </g>
                )} */}

                <g
                  id='municipality-layer'
                  ref={municipalityLayerRef}
                  strokeLinejoin='round'
                  // stroke='#fff'
                  strokeWidth={
                    isNaN(
                      +(layerStyles.municipality?.strokeWidth || 1) /
                        (zoomLevel * 0.8)
                    )
                      ? 1
                      : +(layerStyles.municipality?.strokeWidth || 1) /
                        (zoomLevel * 0.8)
                  }
                />
                {chTopoJSON.objects.lakes && (
                  <g className='lakes-layer'>
                    <path
                      stroke='none'
                      fill='none'
                      {...layerStyles?.lakes}
                      d={
                        path(
                          topojson.feature(chTopoJSON, chTopoJSON.objects.lakes)
                        ) as string
                      }
                    />
                  </g>
                )}
              </g>
              <image
                href={require('./Assets/hills' +
                  (colorMode === 'dark' ? '_dm' : '') +
                  '.png')}
                width={width * 0.915}
                x={130}
                y={61}
                pointerEvents={'none'}
              />
              {chTopoJSON.objects.country && (
                <g className='country-layer'>
                  <path
                    id='country-border'
                    ref={countryBorderRef}
                    stroke='none'
                    fill='none'
                    {...layerStyles?.country}
                    d={
                      path(
                        topojson.feature(chTopoJSON, chTopoJSON.objects.country)
                      ) as string
                    }
                  />
                </g>
              )}

              <g className='hovershape'>
                {hoverShape && (
                  <path
                    pointerEvents={'none'}
                    d={hoverShape}
                    stroke='var(--brandblue-main)'
                    strokeWidth='2'
                    fill='none'
                  />
                )}
                {activeShape && (
                  <>
                    <path
                      pointerEvents={'none'}
                      d={activeShape}
                      stroke='#fff'
                      className='active-municipality'
                      strokeWidth='2'
                      fill='none'
                    />
                  </>
                )}
                {hoverPosition && (
                  <g
                    transform={`translate(${hoverPosition.x},${
                      hoverPosition.y - 25
                    })`}
                    filter={' drop-shadow(0px 0px 1px rgb(0 0 0 / 1))'}
                  >
                    <polygon
                      transform={
                        isZoomedIn ? 'scale(0.25)translate(0,140)' : undefined
                      }
                      points={[
                        [0, 0],
                        [10, 0],
                        [0, 30],

                        [-10, 0],
                        [0, 0],
                      ].join(' ')}
                      fill='#fff'
                    />
                    <circle
                      transform={
                        isZoomedIn ? 'scale(0.25)translate(0,140)' : undefined
                      }
                      r={10}
                      y={-38}
                      fill='#fff'
                    />
                  </g>
                )}
              </g>
            </g>
          </svg>
        )}
        <MapControls
          id='map-controls'
          isFullscreen={isFullscreen}
          ref={mapControlsRef}
        >
          {isZoomable && (
            <>
              <InfoIcon
                role='button'
                // className={!isZoomedIn ? 'disabled' : undefined}
                onClick={() => setShowInfo(!showInfo)}
              />
              <ZoomOutIcon
                role='button'
                className={!isZoomedIn ? 'disabled' : undefined}
                onClick={() => {
                  setIsZoomedIn(false)
                  zoomOut()
                }}
              />
              <ZoomInIcon
                role='button'
                className={zoomLevel >= MAX_ZOOM ? 'disabled' : undefined}
                onClick={() => {
                  setIsZoomedIn(true)
                  activeMunicipalityId
                    ? zoomToActiveMunicipality()
                    : zoomInToWhereIam()
                }}
              />
            </>
          )}
          {showInfo && currentLanguage === 'de' && (
            <>
              <Info onClick={() => setShowInfo(false)}>
                Auf der Karte haben wir die unproduktiven Flächen eingezeichnet.
                Diese werden vom BFS berechnet und beinhalten unbewohnte,
                vegetationslose Flächen (Seen, Gletscher, Fels). Damit
                reduzieren wir den optischen Eindruck der eher dünnbesiedelten
                Berggemeinden und erhöhen so die Lesbarkeit.
              </Info>
            </>
          )}
          {showInfo && currentLanguage === 'fr' && (
            <>
              <Info onClick={() => setShowInfo(false)}>
                Les surfaces improductives ont été représentées sur la carte.
                Calculées par l’OFS, elles regroupent les zones inhabitées et
                sans végétation, comme les lacs, glaciers ou parois rocheuses.
                Leur intégration permet d’atténuer l’effet visuel des communes
                de montagne peu densément peuplées, et ainsi d’améliorer la
                lisibilité générale.{' '}
              </Info>
            </>
          )}
        </MapControls>
      </div>
    </StylesWrapper>
  )
}
const Info = styled.div`
  font-size: 12px;
  line-height: 1.5em;
  position: absolute;
  bottom: calc(100% + 10px);
  right: 30px;
  width: 100%;
  max-width: 250px;
  padding: 15px;
  background-color: var(--info-bg-color);
  cursor: pointer;
  &::after {
    content: 'X';
    background-color: var(--info-bg-color);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    position: absolute;
    right: -30px;
    bottom: 0;
  }
`

const StylesWrapper = styled.div<{
  activeStrokeWidth: string
  activeStroke: string
}>`
  .municipality {
    cursor: pointer;
  }
  .active-municipality {
    stroke-width: 1.5px;
    stroke: #000;
  }
`

const MapControls = styled.div<{ isFullscreen?: boolean }>`
  --brandblue-main: #202346;
  --gray-4: #d4d4d4;
  --gray-5: #e9e9e9;
  display: flex;
  justify-content: flex-end;
  margin-right: ${({ isFullscreen }) => (isFullscreen ? '30px' : '1em')};
  padding-bottom: ${({ isFullscreen }) => (isFullscreen ? '15px' : '0em')};
  transition: padding-bottom 0.3s ease-in-out;
  color: var(--brandblue-main);
  position: sticky;
  z-index: 101;
  gap: 0.5em;
  bottom: 0;
  svg {
    pointer-events: all;

    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    @media (min-width: 535px) {
      &:hover {
        background-color: var(--gray-3);
      }
    }
    @media (max-width: 535px) {
      width: 1.5em;
      height: 1.5em;
    }
    &.disabled {
      opacity: 0.5;
    }
    transition: all 0.3s ease-in-out;
    cursor: pointer;
    background-color: var(--button-color);
    border-radius: 10%;
  }
`

export default React.memo(
  ChoroplethMap,
  (prevProps, nextProps) =>
    prevProps.municipalityData === nextProps.municipalityData &&
    prevProps.activeMunicipalityId === nextProps.activeMunicipalityId &&
    prevProps.year === nextProps.year &&
    prevProps.indexOfPropertyToCompare === nextProps.indexOfPropertyToCompare &&
    prevProps.isFullscreen === nextProps.isFullscreen &&
    nextProps.color === prevProps.color
  // && prevProps.newActiveMunicipalityIdHandler === nextProps.newActiveMunicipalityIdHandler
)
